import { MapPin, Search, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import type { Location } from "@/types/collections"

export function LocationPicker({
  locations,
  searchTerm,
  onSearchChange,
  selectedLocationId,
  onSelect,
}: {
  locations: Location[]
  searchTerm: string
  onSearchChange: (term: string) => void
  selectedLocationId: string
  onSelect: (locationId: string) => void
}) {
  const matching = locations
    .filter((l) => l.status === "active")
    .filter((l) => {
      if (!searchTerm) return true
      const search = searchTerm.toLowerCase()
      return (
        l.site_name?.toLowerCase().includes(search) ||
        l.postcode?.toLowerCase().includes(search) ||
        l.location_id?.toLowerCase().includes(search) ||
        l.address?.toLowerCase().includes(search)
      )
    })

  return (
    <div className="grid gap-2">
      <Label htmlFor="location">Location *</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-palette-jungleGreen400" />
        <Input
          placeholder="Search locations by name, postcode, or ID..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 bg-white border-palette-jungleGreen200 h-9 text-sm"
        />
      </div>
      {selectedLocationId && (
        <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-md border border-amber-200">
          <MapPin size={14} className="text-amber-600" />
          <span className="text-sm font-medium">
            {locations.find((l) => l.id === selectedLocationId)?.site_name || "Selected"}
          </span>
          <span className="text-xs text-palette-jungleGreen500">
            {locations.find((l) => l.id === selectedLocationId)?.postcode}
          </span>
          <button
            type="button"
            onClick={() => onSelect("")}
            className="ml-auto text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="border rounded-md bg-white max-h-[180px] overflow-y-auto">
        {matching.length === 0 ? (
          <div className="p-4 text-center text-palette-jungleGreen500 text-sm">
            {searchTerm ? "No locations match your search" : "No active locations available"}
          </div>
        ) : (
          <div className="divide-y">
            {matching.map((location) => {
              const isSelected = selectedLocationId === location.id
              return (
                <div
                  key={location.id}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-palette-jungleGreen000 transition-colors",
                    isSelected && "bg-amber-50",
                  )}
                  onClick={() => onSelect(location.id)}
                >
                  <MapPin
                    size={14}
                    className={cn("text-palette-jungleGreen400", isSelected && "text-amber-600")}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{location.site_name}</span>
                      {isSelected && (
                        <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded">
                          Selected
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-palette-jungleGreen500">
                      <span>{location.postcode}</span>
                      {location.location_id && (
                        <>
                          <span className="text-palette-jungleGreen300">•</span>
                          <span>{location.location_id}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
